import { Button, EmptyState, ErrorState, Skeleton } from "@helmr/ui";
import { type UseQueryResult } from "@tanstack/react-query";
import { useEffect, useState, type ReactNode } from "react";
import { isApiError } from "../../api/client";
import { useDirectory } from "../directory/useDirectory";
import { NoAccess } from "../../app/NoAccess";
import { useAppContext, useSessionSnapshot } from "../../app/permissions";
import { useServices } from "../../app/services";
import { today as todayOf } from "../../lib/dates";
import s from "./common.module.css";

/** Business, branch and filter in every query key, so a cached list can never be read in another context (tenant guard). */
export function useScope() {
  const { now } = useServices();
  const ctx = useAppContext();
  const snap = useSessionSnapshot();
  const date = todayOf(now(), ctx.timeZone);
  const branchId = snap.branchFilter === "all" ? undefined : snap.branchFilter;
  return { ctx, date, branchId, nowMs: now(), key: [snap.businessId, snap.branchId, snap.branchFilter] as const };
}
export type Scope = ReturnType<typeof useScope>;

/** True from 768px. jsdom has no `matchMedia`, so tests see the phone layout unless they stub it. */
export function useIsWide(): boolean {
  const q = "(min-width: 768px)";
  const get = () => (typeof window !== "undefined" && typeof window.matchMedia === "function" ? window.matchMedia(q).matches : false);
  const [wide, setWide] = useState(get);
  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia(q);
    const on = () => setWide(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  return wide;
}

/**
 * Trainer names for schedules, templates and pickers. They come from `GET /directory`, which needs no `staff.view` and
 * names a trainer for `staff.see_trainer_names` (or `staff.see_names`). With neither, a name reads "Trainer" (or
 * "Trainer (name hidden)" in a picker), never a fragment of the id. `known` is the `trainerName` the record itself carried.
 */
export function useStaffNames() {
  const dir = useDirectory();
  return {
    /** Active people for a trainer picker: `{ id, name }`, hidden names already worded. */
    list: dir.trainerOptions,
    enabled: true,
    nameOf: (id: string | null | undefined, known?: string | null): string => (id ? dir.nameOf(id, "trainer", known)! : "No coach"),
  };
}

export const messageOf = (e: unknown) => (isApiError(e) ? e.message : e instanceof Error ? e.message : "Something went wrong. Try again.");

export function Failure({ error, title, onRetry }: { error: unknown; title: string; onRetry: () => void }) {
  if (isApiError(error) && error.status === 403) return <NoAccess compact />;
  return <ErrorState compact title={title} message={messageOf(error)} requestId={isApiError(error) ? error.requestId : null} onRetry={onRetry} />;
}

export function Gate<T>({ query, skeleton, title, children }: { query: Pick<UseQueryResult<T>, "data" | "error" | "isPending" | "refetch">; skeleton: ReactNode; title: string; children: (d: T) => ReactNode }) {
  if (query.isPending) return <>{skeleton}</>;
  if (query.error) return <Failure error={query.error} title={title} onRetry={() => void query.refetch()} />;
  return <>{children(query.data as T)}</>;
}

export const RowsSkeleton = ({ rows = 5 }: { rows?: number }) => (
  <div aria-busy="true" aria-label="Loading">{Array.from({ length: rows }, (_, i) => <div className={s.skRow} key={i}><Skeleton width="45%" height={13} /><Skeleton width="70%" height={12} /></div>)}</div>
);

export function Field({ label, htmlFor, hint, children }: { label: string; htmlFor?: string; hint?: string; children: ReactNode }) {
  return <div className={s.field}><label htmlFor={htmlFor}>{label}</label>{children}{hint ? <small>{hint}</small> : null}</div>;
}

export const FormError = ({ message }: { message: string | null }) => (message ? <p className={s.err} role="alert">{message}</p> : null);

/** A destructive action asks once, in place. */
export function Confirm({ label, question, onConfirm, busy, variant = "default" }: { label: string; question: string; onConfirm: () => void; busy?: boolean; variant?: "default" | "primary" }) {
  const [asking, setAsking] = useState(false);
  if (!asking) return <Button variant={variant} onClick={() => setAsking(true)}>{label}</Button>;
  return (
    <div className={s.row} role="group" aria-label={question}>
      <span className={s.note}>{question}</span>
      <Button variant="primary" busy={busy} busyLabel="Working…" onClick={onConfirm}>{`Yes, ${label.toLowerCase()}`}</Button>
      <Button variant="plain" onClick={() => setAsking(false)}>Keep</Button>
    </div>
  );
}

export { EmptyState, s as styles };
